import { motion } from "framer-motion";
import { ClipboardList, Coffee, HeartHandshake } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContext";
import { SectionHead } from "@/components/SectionHead";
import { DEFAULT_IMAGES } from "@/config";

const cardAnim = (i) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
});

export const ValueBento = () => {
  const { t } = useLang();
  const { img } = useSite();
  const b = t.bento;

  return (
    <section id="features" className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead kicker={b.kicker} title={b.title} />
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-12">
          <motion.div {...cardAnim(0)} className="flex h-full flex-col justify-between rounded-[2rem] border border-cardline bg-white p-8 sm:p-10 md:col-span-7">
            <div>
              <span className="inline-block rounded-full bg-terra-light px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-terra">{b.parents.tag}</span>
              <h3 className="mt-5 font-display text-2xl font-bold text-ink sm:text-3xl">{b.parents.title}</h3>
              <p className="mt-3 max-w-md leading-relaxed text-inksoft">{b.parents.body}</p>
            </div>
            <img src={img("img_child_tap", DEFAULT_IMAGES.childTap)} alt={b.parents.imgAlt} className="mt-8 h-56 w-full rounded-2xl object-cover sm:h-64" />
          </motion.div>

          <motion.div {...cardAnim(1)} className="relative h-full overflow-hidden rounded-[2rem] bg-pine p-8 text-cream sm:p-10 md:col-span-5">
            <span className="pointer-events-none absolute -bottom-10 end-2 font-display text-[11rem] font-extrabold leading-none text-white/[0.07]">0</span>
            <span className="inline-block rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-sun-light">{b.teachers.tag}</span>
            <h3 className="mt-5 font-display text-2xl font-bold sm:text-3xl">{b.teachers.title}</h3>
            <p className="mt-3 leading-relaxed text-cream/75">{b.teachers.body}</p>
            <Coffee className="mt-8 h-8 w-8 text-sun" />
          </motion.div>

          <motion.div {...cardAnim(2)} className="h-full rounded-[2rem] border border-cardline bg-white p-8 sm:p-10 md:col-span-5">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-pine-light text-pine">
              <ClipboardList className="h-6 w-6" />
            </span>
            <h3 className="mt-6 font-display text-2xl font-bold text-ink sm:text-3xl">{b.admin.title}</h3>
            <p className="mt-3 leading-relaxed text-inksoft">{b.admin.body}</p>
          </motion.div>

          <motion.div {...cardAnim(3)} className="relative min-h-[300px] overflow-hidden rounded-[2rem] md:col-span-7">
            <img src={img("img_gate", DEFAULT_IMAGES.gate)} alt={b.admin.title} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-pine/90 via-pine/35 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-8 sm:p-10">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-cream backdrop-blur">
                <HeartHandshake className="h-4 w-4 text-sun" />
                {b.admin.tag}
              </span>
              <h3 className="mt-4 font-display text-2xl font-bold text-cream sm:text-3xl">{b.admin.title}</h3>
              <p className="mt-2 max-w-md leading-relaxed text-cream/80">{b.admin.body}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
